// @flow
import { config } from '../../Config/ConfigLoader'
import TariffContentInstance from './TariffContentInstance'
import { perUnitOfQuality } from '../ContentFunctions'

const requiredAttributesInQuoteBody = [
  'calculatorName',
  'created',
  'id',
  'price',
  'subProductToDisplayName',
  'subProductToTariffName',
  'subProductToTariffShortName',
  'quoteRequest',
  'price',
]
const optionalAttributesInRawTariffContent = ['partialPrices', 'properties']

// types
import type { PartialPricesType, QuoteBodyType, TariffObjectType } from '../Types/QuoteType'
import type {
  TariffContentWithKeyType,
  TariffKeysType,
  TariffsContentType,
  TariffsSuriIndex,
} from '../Types/TariffsContentType'
import type { CoverageObjectType } from '../Types/CoverageTypes'
import type { ImmutableType } from '../Types/ImmutableTypes'
import type { DynamicSortsType } from '../Types/Sort'

export default class QuoteInstance {
  className: string
  isValid: boolean = false
  hasAllAttributes: boolean = true
  coverageRequired: boolean = false
  isFullContent: boolean = false
  calculatorName: string
  created: string
  id: string
  uniqueQuoteId: string
  price: number
  subProductToDisplayName: TariffObjectType
  subProductToTariffName: TariffObjectType
  subProductToTariffShortName: TariffObjectType
  quoteRequest: { id: string, created: string }
  partialPrices: PartialPricesType
  tariffContent: TariffContentWithKeyType = {}
  anyTariffKey: 'default' | 'tpl' | 'cic'
  allTariffKeys: Array<'default' | 'tpl' | 'cic'>
  tariffKey: TariffKeysType = 'default'
  suriIndex: TariffsSuriIndex
  pricePerUnitOfQuality: number
  sort: { [key: string]: any }
  insuranceCompany: string
  website: number
  invalidBecause: { key: string, data: Object }[] = []

  constructor(data: ?Object) {
    this.className = this.constructor.name
    if (data && typeof data === 'object') {
      Object.keys(data).forEach((key: string) => {
        if (key === 'tariffContent') {
          this.tariffContent = Object.keys(data[key]).reduce((acc, tariffKey) => {
            acc[tariffKey] = data[key][tariffKey]
            if (tariffKey !== 'coverages') {
              acc[tariffKey] = new TariffContentInstance(data[key][tariffKey])
            }
            return acc
          }, {})
        } else {
          this[key] = data[key]
        }
      })
    }
  }

  initFromRawQuote = (quoteBody: QuoteBodyType, tariffKey: TariffKeysType, website: number) => {
    this.tariffKey = tariffKey
    this.website = website
    requiredAttributesInQuoteBody.forEach((attributeName) => {
      if (typeof quoteBody[attributeName] !== 'undefined' && attributeName !== 'quoteRequest') {
        this[attributeName] = quoteBody[attributeName]
      } else if (attributeName === 'quoteRequest' && typeof quoteBody[attributeName] === 'object') {
        this.quoteRequest = {
          id: quoteBody[attributeName].id,
          created: quoteBody[attributeName].created,
        }
      } else {
        this.hasAllAttributes = false
        if (typeof quoteBody.errors !== 'undefined') {
          quoteBody.errors.map((error) => {
            this.invalidBecause.push({
              key: error.validationError,
              data: {
                key: error.fieldName,
              },
            })
          })
        } else {
          this.invalidBecause.push({
            key: 'INVALID_QUOTE_BODY',
            data: {
              key: attributeName,
            },
          })
        }
      }
    })
    optionalAttributesInRawTariffContent.forEach((attributeName) => {
      if (typeof quoteBody[attributeName] !== 'undefined') {
        this[attributeName] = quoteBody[attributeName]
      }
    })
    this.coverageRequired = config.getConfig('productName') !== 'travel'
    this.allTariffKeys = Object.keys(quoteBody.subProductToTariffName)
    this.anyTariffKey = this.allTariffKeys[0]
    this.uniqueQuoteId = JSON.stringify(quoteBody.subProductToTariffName)
    this.suriIndex = {
      value: 0,
    }
    this.pricePerUnitOfQuality = 0
    return this
  }

  setCoverages = (coverageLimits: CoverageObjectType) => {
    let validCoverageLimits = false
    if (coverageLimits && typeof coverageLimits === 'object' && Object.keys(coverageLimits).length > 0) {
      this.tariffContent.coverages = coverageLimits
      validCoverageLimits = true
    }
    this.isValid =
      this.isFullContent &&
      (!this.coverageRequired || (this.coverageRequired && validCoverageLimits)) &&
      this.hasAllAttributes
    return this
  }

  setTariffContent = (allTariffsContent: TariffsContentType & ImmutableType, formData: Object) => {
    if (this.allTariffKeys.length > 0) {
      this.isFullContent = true
      this.tariffContent = {
        coverages: this.tariffContent.coverages || {},
        ...this.allTariffKeys.reduce((acc, tariffKey) => {
          if (!allTariffsContent[this.subProductToTariffName[tariffKey]]) {
            this.isFullContent = false
            this.invalidBecause.push({
              key: 'MISSING_QUOTE_CONTENT',
              data: {
                subProductToTariffName: !this.subProductToTariffName[tariffKey],
                tariffsContent: !allTariffsContent[this.subProductToTariffName[tariffKey]],
              },
            })
          } else {
            const contentInstance = new TariffContentInstance().initFromRawTariffContent(
              allTariffsContent[this.subProductToTariffName[tariffKey]],
              formData,
              this.website,
            )
            if (contentInstance.isValid) {
              this.insuranceCompany = contentInstance.insuranceCompany
              if (tariffKey === 'cic' && this.properties && this.properties.directDecreasingCascoIncluded) {
                contentInstance.properties = contentInstance.properties.setIn(
                  ['priceIncludes', contentInstance.properties.priceIncludes.length],
                  'directDecreasingCasco',
                )
                if (contentInstance.properties.priceIncludesDynamic) {
                  contentInstance.properties = contentInstance.properties.setIn(
                    ['priceIncludesDynamic', contentInstance.properties.priceIncludesDynamic.length],
                    { type: ['car', 'van', 'motorcycle', 'truck', 'rig'], values: ['directDecreasingCasco'] },
                  )
                }
              }
              acc[tariffKey] = contentInstance
            } else {
              this.isFullContent = false
            }
            this.invalidBecause = [
              ...this.invalidBecause,
              ...contentInstance.invalidBecause.map((i) => ({ ...i, data: { ...i.data, tariffKey } })),
            ]
          }
          return acc
        }, {}),
      }
      if (this.isFullContent) {
        this.initQuoteWithContent()
      }
    }
    this.isValid =
      ((this.coverageRequired && !!this.tariffContent.coverages) || !this.coverageRequired) &&
      this.isFullContent &&
      this.hasAllAttributes
    return this
  }

  initQuoteWithContent = () => {
    const actualTariffContent: TariffContentInstance = this.tariffContent[this.tariffKey]
    this.sort = {}
    if (this.tariffKey !== 'tplCic') {
      this.suriIndex = {
        ...this.tariffContent[this.tariffKey].suriIndex,
      }
      this.pricePerUnitOfQuality = perUnitOfQuality(this.price, this.suriIndex.value)
      this.sort.price = actualTariffContent.getSortValue('price', this.price)
      this.sort.totalLimitsValueTpl =
        this.tariffKey === 'tpl'
          ? actualTariffContent.getSortValue(
              'totalLimitsValueTpl',
              actualTariffContent.getLimitPropertyValue('totalValue'),
            )
          : 0
      this.sort.totalLimitsValueCic =
        this.tariffKey === 'cic'
          ? actualTariffContent.getSortValue(
              'totalLimitsValueCic',
              actualTariffContent.getLimitPropertyValue('totalValue'),
            )
          : 0
      const dynamicSorts: DynamicSortsType = config.getConfig('quoteListContainer.dynamicSorts')
      if (dynamicSorts) {
        dynamicSorts.forEach((dynamicSort) => {
          // $FlowIssue
          if (actualTariffContent.getSortValue(dynamicSort.newValuePath)) {
            this.sort[dynamicSort.newValuePath] = actualTariffContent.getSortValue(dynamicSort.newValuePath)
          } else {
            const dynamicSortValue = dynamicSort.originalPath.split('.').reduce((acc, attrName) => {
              if (typeof acc[attrName] !== 'undefined') {
                return acc[attrName]
              }
              return acc
            }, actualTariffContent)
            this.sort[dynamicSort.newValuePath] = typeof dynamicSortValue !== 'object' ? dynamicSortValue : ''
          }
        })
      }
    } else {
      const tplSuriIndex = this.tariffContent.tpl.getSuriIndexValue()
      const cicSuriIndex = this.tariffContent.cic.getSuriIndexValue()
      this.suriIndex = {
        ...this.tariffContent.tpl.suriIndex,
        ...this.tariffContent.cic.suriIndex,
        value: Math.round(((tplSuriIndex + cicSuriIndex) / 2 + 1) * 100) / 100,
      }
      this.sort.price = this.price
      this.pricePerUnitOfQuality = perUnitOfQuality(this.price, this.suriIndex.value)
    }
    this.sort.pricePerUnitOfQualityForSort = this.suriIndex.sort ? this.suriIndex.sort : this.pricePerUnitOfQuality
  }
}
